import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import FollowupBriefPanel from '../../components/commitments/FollowupBriefPanel';
import InteractionRecorder from '../../components/commitments/InteractionRecorder';
import CaseAppointmentsPanel from '../../components/scheduling/CaseAppointmentsPanel';
import Modal from '../../components/Modal';
import ReturnToClientModal from '../../components/workflow/ReturnToClientModal';
import EddEvidencePanel from '../../components/workflow/EddEvidencePanel';
import SimpleReasonModal from '../../components/workflow/SimpleReasonModal';
import FieldReviewSection, { type ReviewableFile } from '../../components/staff/FieldReviewSection';
import DocumentViewerPane from '../../components/staff/DocumentViewerPane';
import KycReviewAssistant from '../../components/staff/KycReviewAssistant';
import contractsService, { type SignedDocument } from '../../services/contracts.service';
import workflowService from '../../services/workflow.service';
import { useAuthStore } from '../../stores/authStore';
import type {
  CaseDetailFull,
  TimelineEvent,
} from '../../types/workflowDetail';
import type { CaseSummary } from '../../types/contracts';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { formatFieldValue, type FormFieldDef } from '../../lib/workflowForm';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';

const CONTRACT_WORKFLOW_SLUG = 'contract_review_v1';

const STATE_BADGE: Record<string, { bg: string; fg: string }> = {
  // contract_review_v1
  pending_upload:       { bg: colors.warningBg, fg: colors.warning },
  uploaded:             { bg: '#EEF2FF',         fg: '#4338CA' },
  returned_to_client:   { bg: colors.dangerBg,   fg: colors.danger },
  pending_mgr:          { bg: '#EEF2FF',         fg: '#4338CA' },
  returned_to_agent:    { bg: '#EEF2F7',         fg: '#475569' },
  approved:             { bg: colors.successBg,  fg: colors.success },
  rejected:             { bg: colors.dangerBg,   fg: colors.danger },
  // onboarding_retail (KYC)
  registered:           { bg: '#EEF2F7',         fg: '#475569' },
  pending_profile:      { bg: '#EEF2F7',         fg: '#475569' },
  pending_pep_aml:      { bg: '#EEF2F7',         fg: '#475569' },
  pending_documents:    { bg: '#EEF2F7',         fg: '#475569' },
  under_review:         { bg: '#EEF2FF',         fg: '#4338CA' },
  information_required: { bg: colors.warningBg,  fg: colors.warning },
  // Same indigo as `under_review`: signals "in active review", but on
  // the supervisor's plate now, not the agent's. Distinct from the
  // neutral grey of `registered` / `archived` / `returned_to_agent`.
  pending_decision:     { bg: '#EEF2FF',         fg: '#4338CA' },
  pending_rejection:    { bg: colors.dangerBg,   fg: colors.danger },
  restricted:           { bg: colors.warningBg,  fg: colors.warning },
  archived:             { bg: '#EEF2F7',         fg: '#475569' },
};

/** Fallback used only when the backend payload predates the
 *  `workflow_name` field. New callers should pull `caseDetail.workflow_name`
 *  directly so the human-friendly DB name surfaces. */
function humaniseWorkflow(slug: string): string {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface QuickAction {
  /** event_trigger sent to /workflow/cases/{id}/transition */
  event: string;
  /** Button label shown to staff */
  label: string;
  /** Visual emphasis: primary actions stand out, secondary fade in */
  variant: 'primary' | 'secondary' | 'danger';
  /** Optional confirm prompt before firing — keeps simple no-input
   *  transitions safe without a full modal. */
  confirmPrompt: string | null;
}

/**
 * event_trigger → button config for actions that need NO text input
 * (just a confirm). Anything requiring a reason or curated message is
 * a MODAL_ACTION instead.
 */
const QUICK_ACTIONS: Record<string, QuickAction> = {
  // contract_review_v1
  'manager:approve': {
    event: 'manager:approve',
    label: 'Approve',
    variant: 'primary',
    confirmPrompt: 'Approve this contract? The member will be notified.',
  },
  'agent:escalate': {
    event: 'agent:escalate',
    label: 'Escalate to manager',
    variant: 'primary',
    confirmPrompt: 'Send this contract to your manager for approval?',
  },
  'agent:resubmit': {
    event: 'agent:resubmit',
    label: 'Resubmit to manager',
    variant: 'primary',
    confirmPrompt: 'Resubmit this contract for manager approval?',
  },
  // onboarding_retail (KYC) — the seed defines APPROVE with
  // requires_reason=false, so it surfaces as a quick-confirm.
  APPROVE: {
    event: 'APPROVE',
    label: 'Approve KYC',
    variant: 'primary',
    confirmPrompt: 'Approve this KYC submission? The member will be notified.',
  },
};

/**
 * Events that open a modal for text input. The modal kind determines which
 * component renders (see modal switch in the page body).
 */
// `kyc_request_info` was retired with the per-field review feature
// (turn 2). REQUEST_INFO is now a gated direct-fire — the backend
// auto-builds the curated message from per-field rejection reasons,
// so no modal needs to capture a top-level summary anymore.
type ModalKind =
  | 'return_to_client'
  | 'return_to_agent'
  | 'reject'
  | 'kyc_reject'
  | 'kyc_propose_rejection'
  | 'kyc_approve_rejection'
  | 'kyc_override_rejection'
  | 'kyc_restrict'
  | 'kyc_clear'
  | 'kyc_archive'
  | 'kyc_reopen';

interface ModalAction {
  label: string;
  variant: 'secondary' | 'danger';
  modal: ModalKind;
}

const MODAL_ACTIONS: Record<string, ModalAction> = {
  // contract_review_v1
  'agent:return_to_client': {
    label: 'Return to member',
    variant: 'secondary',
    modal: 'return_to_client',
  },
  'manager:return_to_agent': {
    label: 'Return to agent',
    variant: 'secondary',
    modal: 'return_to_agent',
  },
  'manager:reject': {
    label: 'Reject',
    variant: 'danger',
    modal: 'reject',
  },
  // onboarding_retail (KYC) — all reuse SimpleReasonModal because
  // the curated client-facing message is contract-only (the
  // backend's _CLIENT_FACING_RETURN_STATES is just `returned_to_client`).
  REJECT: {
    label: 'Reject KYC',
    variant: 'danger',
    modal: 'kyc_reject',
  },
  // REQUEST_INFO has no MODAL_ACTIONS entry by design: the
  // per-field review feature replaced it with a gated direct-fire
  // button handled in the availableEvents render loop. The backend
  // auto-builds client_visible_notes from the per-field rejections.
  //
  // PROPOSE_REJECTION also has no entry here — it is gated by
  // allFieldsDecided and rendered explicitly in the loop below.
  APPROVE_REJECTION: {
    label: 'Approve rejection',
    variant: 'danger',
    modal: 'kyc_approve_rejection',
  },
  OVERRIDE_REJECTION: {
    label: 'Override (cancel proposal)',
    variant: 'secondary',
    modal: 'kyc_override_rejection',
  },
  RESTRICT: {
    label: 'Restrict (compliance)',
    variant: 'danger',
    modal: 'kyc_restrict',
  },
  // SUBMIT_FOR_DECISION has no MODAL_ACTIONS entry — it's a gated
  // direct-fire handled in the availableEvents render loop below.
  //
  // NO agent-fired recall (RETURN_TO_REVIEW) is registered. Agent
  // escalation is one-way per the locked SOD rule (memory
  // kyc_review_actions_model decision #5). If a manager-fired
  // RETURN_TO_AGENT is ever needed, register it here following the
  // OVERRIDE_REJECTION template.
  CLEAR: {
    label: 'Clear restriction',
    variant: 'secondary',
    modal: 'kyc_clear',
  },
  ARCHIVE: {
    label: 'Archive',
    variant: 'secondary',
    modal: 'kyc_archive',
  },
  // REOPEN — manager/super_admin reopens a TERMINAL case (approved/rejected/
  // archived) back to under_review. requires_reason=true so it reuses
  // SimpleReasonModal, mirroring the OVERRIDE_REJECTION template. Backend
  // transition seeded in migration 0004_reopen_kyc.
  REOPEN: {
    label: 'Reopen case',
    variant: 'secondary',
    modal: 'kyc_reopen',
  },
};

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(readerLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatBytes(b: number | null | undefined): string {
  if (b === null || b === undefined) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function buttonStyle(variant: 'primary' | 'secondary' | 'danger', disabled: boolean): React.CSSProperties {
  if (variant === 'primary') {
    return {
      background: colors.navy,
      color: '#FFFFFF',
      border: 'none',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
    };
  }
  if (variant === 'danger') {
    return {
      background: colors.surface,
      color: colors.danger,
      border: `1px solid ${colors.dangerBorder}`,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
    };
  }
  return {
    background: colors.surface,
    color: colors.inkSoft,
    border: `1px solid ${colors.border}`,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function SendBackButton({
  actionInFlight,
  canSendBack,
  allDecided,
  anyRejected,
  onClick,
}: {
  readonly actionInFlight: string | null;
  readonly canSendBack: boolean;
  readonly allDecided: boolean;
  readonly anyRejected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const isWorking = actionInFlight === 'REQUEST_INFO';
  const disabled = isWorking || actionInFlight !== null || !canSendBack;
  let tooltip: string;
  if (!allDecided) {
    tooltip = 'Approve or reject every field and document first.';
  } else if (anyRejected) {
    tooltip = 'Send the case back to the member with the per-field rejection list.';
  } else {
    tooltip = 'Nothing is rejected — use Submit to supervisor instead.';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
      style={buttonStyle('secondary', disabled)}
    >
      {isWorking ? 'Sending…' : 'Send back to member'}
    </button>
  );
}

function SubmitForDecisionButton({
  actionInFlight,
  canSubmitForDecision,
  allDecided,
  anyRejected,
  onClick,
}: {
  readonly actionInFlight: string | null;
  readonly canSubmitForDecision: boolean;
  readonly allDecided: boolean;
  readonly anyRejected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const isWorking = actionInFlight === 'SUBMIT_FOR_DECISION';
  const disabled = isWorking || actionInFlight !== null || !canSubmitForDecision;
  let tooltip: string;
  if (!allDecided) {
    tooltip = 'Approve or reject every field and document first.';
  } else if (anyRejected) {
    tooltip = 'Items are rejected — use Send back to member or Propose rejection.';
  } else {
    tooltip = 'Hand the case off to a supervisor for the final decision.';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
      style={buttonStyle('primary', disabled)}
    >
      {isWorking ? 'Submitting…' : 'Submit to supervisor'}
    </button>
  );
}

function ProposeRejectionButton({
  actionInFlight,
  canProposeRejection,
  allDecided,
  onClick,
}: {
  readonly actionInFlight: string | null;
  readonly canProposeRejection: boolean;
  readonly allDecided: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const disabled = actionInFlight !== null || !canProposeRejection;
  const tooltip = allDecided
    ? 'Propose rejecting this application — a manager will review and decide.'
    : 'Review every field and document before proposing rejection.';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
      style={buttonStyle('danger', disabled)}
    >
      Propose rejection
    </button>
  );
}

/** Per-field/per-document review gate driving the staff toolbar. Mirrors the
 *  backend rules in _build_curated_message_from_reviews / _guard_submit_for_decision:
 *  every field AND active document must be decided; REQUEST_INFO needs ≥1
 *  rejection, SUBMIT_FOR_DECISION needs zero, PROPOSE_REJECTION ignores it. */
function computeReviewGate(
  caseDetail: CaseDetailFull,
  isReviewableKyc: boolean,
): {
  allDecided: boolean;
  anyRejected: boolean;
  decidedCount: number;
  reviewTotal: number;
  canSendBack: boolean;
  canProposeRejection: boolean;
  canSubmitForDecision: boolean;
} {
  const reviewMap = caseDetail.field_reviews;
  const reviewKeys = Object.keys(reviewMap);
  const allFieldsDecided =
    reviewKeys.length > 0 &&
    reviewKeys.every((k) => reviewMap[k].review_status !== 'pending');
  const someFieldRejected = reviewKeys.some((k) => reviewMap[k].review_status === 'rejected');
  // An empty documents[] trivially passes — KYC states needing no upload are fine.
  const docs = caseDetail.documents ?? [];
  const allDocsDecided = docs.every((d) => d.status === 'valid' || d.status === 'invalid');
  const someDocRejected = docs.some((d) => d.status === 'invalid');
  const allDecided = allFieldsDecided && allDocsDecided;
  const anyRejected = someFieldRejected || someDocRejected;
  const decidedCount =
    reviewKeys.filter((k) => reviewMap[k].review_status !== 'pending').length +
    docs.filter((d) => d.status === 'valid' || d.status === 'invalid').length;
  return {
    allDecided,
    anyRejected,
    decidedCount,
    reviewTotal: reviewKeys.length + docs.length,
    canSendBack: isReviewableKyc && allDecided && anyRejected,
    canProposeRejection: isReviewableKyc && allDecided,
    canSubmitForDecision: isReviewableKyc && allDecided && !anyRejected,
  };
}

/** Document download actions (rendered PDF + signed documents). Surfaces
 *  failures through the page's action-error channel. */
function useCaseDownloads(
  caseId: string | undefined,
  onError: (msg: string) => void,
): {
  downloadingDocId: string | null;
  handleDownloadRendered: () => Promise<void>;
  handleDownloadSigned: (documentId: string) => Promise<void>;
} {
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const handleDownloadRendered = async (): Promise<void> => {
    if (!caseId) return;
    try {
      const { download_url } = await contractsService.getDownloadUrl(caseId);
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      onError(getApiErrorMessage(err, 'Could not generate the download link.'));
    }
  };
  const handleDownloadSigned = async (documentId: string): Promise<void> => {
    if (!caseId) return;
    setDownloadingDocId(documentId);
    try {
      const { download_url } = await contractsService.getSignedDocumentDownloadUrl(caseId, documentId);
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      onError(getApiErrorMessage(err, 'Could not generate the signed-document link.'));
    } finally {
      setDownloadingDocId(null);
    }
  };
  return { downloadingDocId, handleDownloadRendered, handleDownloadSigned };
}

/** Transient flash (success) + error banners for case actions. */
function CaseAlerts({
  actionFlash,
  actionError,
  onDismissFlash,
}: {
  readonly actionFlash: string | null;
  readonly actionError: string | null;
  readonly onDismissFlash: () => void;
}): JSX.Element {
  return (
    <>
      {actionFlash && (
        <output
          className="rounded-lg p-3 mb-4 text-sm flex items-start justify-between gap-3"
          style={{ background: colors.successBg, color: colors.success, border: `1px solid #A7F3D0` }}
        >
          <span>{actionFlash}</span>
          <button
            type="button"
            onClick={onDismissFlash}
            className="text-xs underline flex-shrink-0"
            style={{ color: colors.success }}
          >
            Dismiss
          </button>
        </output>
      )}
      {actionError && (
        <div
          className="rounded-lg p-3 mb-4 text-sm"
          style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
          role="alert"
        >
          {actionError}
        </div>
      )}
    </>
  );
}

/** Shown to an agent viewing a case they already handed off — review controls
 *  are locked but they can still log interactions. */
function AgentHandoffBanner({ state }: { readonly state: string }): JSX.Element {
  return (
    <output
      className="rounded-lg p-3 mb-4 text-sm flex items-start gap-3"
      style={{ background: '#EEF2FF', color: '#3730A3', border: '1px solid #C7D2FE' }}
    >
      <span style={{ fontSize: 18, lineHeight: '20px' }} aria-hidden="true">👁</span>
      <div>
        <div className="font-semibold">
          {state === 'pending_decision'
            ? 'This case is with a supervisor for the final decision.'
            : 'You proposed rejection — awaiting supervisor confirmation.'}
        </div>
        <div className="mt-0.5" style={{ color: '#4338CA' }}>
          The review controls are locked. You can still log a client interaction
          below or open documents to review your earlier decisions.
        </div>
      </div>
    </output>
  );
}

/** Member + Assigned-to summary cards. */
function CaseParties({ caseDetail }: { readonly caseDetail: CaseDetailFull }): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <section className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <h2 className="text-sm font-semibold mb-2" style={{ color: colors.muted }}>MEMBER</h2>
        <div className="font-medium" style={{ color: colors.ink }}>
          {caseDetail.user ? fullName(caseDetail.user) || '—' : '—'}
        </div>
        <div className="text-sm" style={{ color: colors.inkSoft }}>
          {caseDetail.user?.email ?? '—'}
        </div>
        <div className="text-xs mt-3" style={{ color: colors.mutedSoft }}>
          Case ID: {caseDetail.case_id}
        </div>
      </section>
      <section className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <h2 className="text-sm font-semibold mb-2" style={{ color: colors.muted }}>ASSIGNED TO</h2>
        <div className="font-medium" style={{ color: colors.ink }}>
          {caseDetail.assigned_to ? fullName(caseDetail.assigned_to) : (
            <span style={{ color: colors.mutedSoft }}>Unassigned</span>
          )}
        </div>
        <div className="text-xs mt-3" style={{ color: colors.mutedSoft }}>
          Status: {caseDetail.status}
          {caseDetail.expires_at && ` · expires ${formatDateTime(caseDetail.expires_at)}`}
        </div>
      </section>
    </div>
  );
}

/** The case-data review surface: KYC per-field review (with optional document
 *  viewer split) for reviewable KYC cases, else the read-only captured-data
 *  section. Renders nothing for contract cases (they show DOCUMENTS instead). */
function CaseDataReview({
  caseDetail,
  isContractCase,
  isReviewableKyc,
  viewerFile,
  onViewDocument,
  fieldReviewReadOnly,
  role,
  onReviewChanged,
  onError,
}: {
  readonly caseDetail: CaseDetailFull;
  readonly isContractCase: boolean;
  readonly isReviewableKyc: boolean;
  readonly viewerFile: ReviewableFile | null;
  readonly onViewDocument: (file: ReviewableFile | null) => void;
  readonly fieldReviewReadOnly: boolean;
  readonly role?: string;
  readonly onReviewChanged: () => void;
  readonly onError: (msg: string) => void;
}): JSX.Element | null {
  if (isContractCase) return null;
  if (!isReviewableKyc) {
    // Read-only captured data. Personal documents open in the same viewer pane
    // (View) for agents/managers; only super_admin gets a Download.
    return (
      <div
        className="grid gap-4 mb-4"
        style={{ gridTemplateColumns: viewerFile ? 'minmax(0, 1fr) minmax(0, 0.7fr)' : '1fr' }}
      >
        <div className="min-w-0">
          <CapturedDataSection
            caseId={caseDetail.case_id}
            data={caseDetail.data}
            fields={caseDetail.fields}
            role={role}
            onViewDocument={onViewDocument}
            onError={onError}
          />
        </div>
        {viewerFile && (
          <div className="min-w-0">
            <DocumentViewerPane
              caseId={caseDetail.case_id}
              file={viewerFile}
              onClose={() => onViewDocument(null)}
            />
          </div>
        )}
      </div>
    );
  }
  return (
    <>
      <KycReviewAssistant caseId={caseDetail.case_id} />
      <div
        className="grid gap-4 mb-4"
        style={{ gridTemplateColumns: viewerFile ? 'minmax(0, 1fr) minmax(0, 0.7fr)' : '1fr' }}
      >
        <div className="min-w-0">
          <FieldReviewSection
            caseId={caseDetail.case_id}
            detail={caseDetail}
            onReviewChanged={onReviewChanged}
            onViewDocument={onViewDocument}
            selectedDocumentId={viewerFile?.documentId ?? null}
            readOnly={fieldReviewReadOnly}
          />
        </div>
        {viewerFile && (
          <div className="min-w-0">
            <DocumentViewerPane
              caseId={caseDetail.case_id}
              file={viewerFile}
              onClose={() => onViewDocument(null)}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default function StaffCaseDetail(): JSX.Element {
  const { caseId } = useParams<{ caseId: string }>();
  const location = useLocation();
  const { user } = useAuthStore();
  // Determine which queue to return to based on URL prefix
  const backHref = location.pathname.startsWith('/manager/')
    ? '/manager/review-queue'
    : '/agent/review-queue';

  const [caseDetail, setCaseDetail] = useState<CaseDetailFull | null>(null);
  const [contractSummary, setContractSummary] = useState<CaseSummary | null>(null);
  const [signedDocs, setSignedDocs] = useState<SignedDocument[] | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionFlash, setActionFlash] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<ModalKind | null>(null);
  // Document currently shown in the right-side viewer pane (KYC review only).
  const [viewerFile, setViewerFile] = useState<ReviewableFile | null>(null);
  const { downloadingDocId, handleDownloadRendered, handleDownloadSigned } = useCaseDownloads(
    caseId,
    setActionError,
  );

  const loadAll = useCallback(async () => {
    if (!caseId) return;
    setLoadError(null);
    try {
      // Load workflow-generic data first to know which extras to fetch.
      // The contracts/* endpoints 404 for non-contract workflows, so we
      // can't blindly Promise.all everything.
      const [detail, tl] = await Promise.all([
        workflowService.getCaseFull(caseId),
        contractsService.getTimeline(caseId),
      ]);
      setCaseDetail(detail);
      setTimeline(tl);

      if (detail.workflow_slug === CONTRACT_WORKFLOW_SLUG) {
        const [summary, docs] = await Promise.all([
          contractsService.getCase(caseId),
          contractsService.listSignedDocuments(caseId),
        ]);
        setContractSummary(summary);
        setSignedDocs(docs);
      } else {
        setContractSummary(null);
        setSignedDocs(null);
      }
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Could not load this case.'));
    }
  }, [caseId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Auto-open the ID front in the viewer the first time a reviewable KYC case
  // loads, so the agent reads the ID up front and validates the captured name /
  // CURP / etc. against it. Once per case (a ref, so closing it or a post-review
  // refetch doesn't re-pop the pane); switching cases re-triggers.
  const autoOpenedCaseRef = useRef<string | null>(null);
  useEffect(() => {
    if (!caseDetail || !caseId || autoOpenedCaseRef.current === caseId) return;
    const reviewable =
      caseDetail.workflow_slug !== CONTRACT_WORKFLOW_SLUG &&
      ['under_review', 'information_required', 'pending_decision'].includes(
        caseDetail.current_state_slug,
      );
    if (!reviewable) return;
    const idFront = caseDetail.data['id_front'];
    if (idFront && typeof idFront === 'object' && 'document_id' in idFront) {
      autoOpenedCaseRef.current = caseId;
      const fieldDef = caseDetail.fields.find((f) => f.field_key === 'id_front');
      setViewerFile({
        fieldKey: 'id_front',
        label: fieldDef?.label ?? 'ID front',
        documentId: (idFront as { document_id: string }).document_id,
        mimeType: (idFront as { mime_type?: string }).mime_type,
      });
    }
  }, [caseDetail, caseId]);

  async function handleQuickAction(action: QuickAction): Promise<void> {
    if (!caseId) return;
    if (action.confirmPrompt && !globalThis.confirm(action.confirmPrompt)) return;
    setActionError(null);
    setActionInFlight(action.event);
    try {
      await workflowService.applyTransition(caseId, { event_trigger: action.event });
      setActionFlash(`Action "${action.label}" applied successfully.`);
      await loadAll();
    } catch (err: unknown) {
      setActionError(getApiErrorMessage(err, 'Could not apply this action.'));
    } finally {
      setActionInFlight(null);
    }
  }

  if (!caseId) {
    return <p style={{ color: colors.muted }}>Missing case identifier.</p>;
  }

  if (loadError) {
    return (
      <>
        <PageHeader title="Case" />
        <div
          className="rounded-2xl p-6 mb-4"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          {loadError}
        </div>
        <Link to={backHref} className="text-sm" style={{ color: colors.ink }}>
          ← Back to queue
        </Link>
      </>
    );
  }

  if (!caseDetail || timeline === null) {
    return (
      <>
        <PageHeader title="Case" />
        <p className="text-sm" style={{ color: colors.muted }}>
          Loading…
        </p>
      </>
    );
  }

  const isContractCase = caseDetail.workflow_slug === CONTRACT_WORKFLOW_SLUG;
  // KYC case in a state where the per-field review UI is active.
  // Matches `_REVIEWABLE_STATES` on the backend (under_review +
  // information_required + pending_decision). Drives split layout and
  // the gating of the "Send back to client", "Propose rejection", and
  // "Submit to supervisor" buttons. `pending_decision` is included so
  // the manager sees the field/doc review pane when deciding, and so
  // the submitting agent sees their own decisions in a read-only view.
  // `pending_rejection` is intentionally excluded — the proposal is
  // already submitted and no per-field edits are possible there.
  const isReviewableKyc =
    !isContractCase &&
    (caseDetail.current_state_slug === 'under_review' ||
      caseDetail.current_state_slug === 'information_required' ||
      caseDetail.current_state_slug === 'pending_decision');

  // Role-aware read-only flag for the per-field review pane.
  // Matches the workflow.state_permissions matrix exactly:
  //   · under_review        → agent + manager can edit
  //   · pending_decision    → manager + super_admin only
  //   · pending_rejection   → manager + super_admin only
  //   · information_required → nobody edits reviews (client edits data)
  // When false the FieldReviewSection renders the same UI but with every
  // Approve/Reject control disabled and a "case is with supervisor"
  // tooltip — matches what the backend would 403 on if the agent tried.
  const role = user?.role;
  const state = caseDetail.current_state_slug;
  const isStaffEditor = role === 'manager' || role === 'super_admin';
  const fieldReviewReadOnly = !(
    state === 'under_review'
      ? role === 'agent' || isStaffEditor
      : (state === 'pending_decision' || state === 'pending_rejection') && isStaffEditor
  );

  // Agent-facing affordance: shown when the agent has handed off the
  // case to the supervisor and is viewing it after the fact. Sets
  // expectations ("you can no longer modify, but you can log calls").
  // Manager / super_admin viewing the same state sees the standard
  // toolbar; this banner is suppressed for them.
  const showAgentHandoffBanner =
    role === 'agent' &&
    (state === 'pending_decision' || state === 'pending_rejection');

  // Per-field/per-document review gate that controls the staff
  // toolbar. Mirrors the backend rule in
  // `_build_curated_message_from_reviews` and `_guard_submit_for_decision`:
  // every field AND every active document for the state must be
  // decided. REQUEST_INFO additionally needs ≥1 rejection;
  // SUBMIT_FOR_DECISION needs ZERO rejections; PROPOSE_REJECTION
  // ignores the rejection count (AML reasons live outside per-field
  // review). The frontend gate is purely UX — backend 409s on
  // violation either way.
  // Per-field/per-document review gate (mirrors backend guards) — see helper.
  const {
    allDecided, anyRejected, decidedCount, reviewTotal,
    canSendBack, canProposeRejection, canSubmitForDecision,
  } = computeReviewGate(caseDetail, isReviewableKyc);

  // Direct-fire of REQUEST_INFO (no modal). The backend builds the
  // curated client_visible_notes from the per-field rejection
  // reasons; the agent doesn't write any free-form summary here.
  async function handleSendBack(): Promise<void> {
    if (!caseId) return;
    setActionError(null);
    setActionInFlight('REQUEST_INFO');
    try {
      await workflowService.applyTransition(caseId, {
        event_trigger: 'REQUEST_INFO',
        notes: 'Per-field review — see client_visible_notes for the curated list.',
      });
      setActionFlash('Returned to member with the per-field rejection list.');
      setViewerFile(null);
      await loadAll();
    } catch (err: unknown) {
      setActionError(getApiErrorMessage(err, 'Could not send the case back.'));
    } finally {
      setActionInFlight(null);
    }
  }

  // Direct-fire of SUBMIT_FOR_DECISION (no modal). Closes the
  // workflow hole where a clean agent review pass had nowhere to
  // go — the manager had to guess from the queue. Now the agent
  // explicitly hands the case off; the audit log captures who
  // finished review and when.
  async function handleSubmitForDecision(): Promise<void> {
    if (!caseId) return;
    setActionError(null);
    setActionInFlight('SUBMIT_FOR_DECISION');
    try {
      await workflowService.applyTransition(caseId, {
        event_trigger: 'SUBMIT_FOR_DECISION',
      });
      setActionFlash('Submitted to supervisor for the final decision.');
      setViewerFile(null);
      await loadAll();
    } catch (err: unknown) {
      setActionError(
        getApiErrorMessage(err, 'Could not submit for supervisor review.'),
      );
    } finally {
      setActionInFlight(null);
    }
  }
  // For non-contract workflows, the page title falls back to a humanised
  // workflow slug — we don't have a "template title" in the KYC world.
  const pageTitle = isContractCase && contractSummary
    ? contractSummary.template_title
    : (caseDetail.workflow_name || humaniseWorkflow(caseDetail.workflow_slug));
  const stateBadge = STATE_BADGE[caseDetail.current_state_slug] ?? { bg: '#EEF2F7', fg: '#475569' };
  const availableEvents = caseDetail.available_events;

  return (
    <>
      <PageHeader
        title={pageTitle}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-block px-2 py-0.5 rounded text-xs font-medium"
              style={{ background: stateBadge.bg, color: stateBadge.fg }}
            >
              {caseDetail.current_state_slug.replace(/_/g, ' ')}
            </span>
            <span className="text-xs" style={{ color: colors.mutedSoft }}>
              · {caseDetail.workflow_slug} v{caseDetail.workflow_version}
            </span>
            <span className="text-xs" style={{ color: colors.mutedSoft }}>
              · last activity {formatDateTime(caseDetail.last_activity_at)}
            </span>
          </span>
        }
        actions={
          <Link to={backHref} className="text-sm" style={{ color: colors.muted }}>
            ← Back to queue
          </Link>
        }
      />

      <CaseAlerts
        actionFlash={actionFlash}
        actionError={actionError}
        onDismissFlash={() => setActionFlash(null)}
      />

      {/* Pre-call brief — renders only on deposit_followup_v1 cases. */}
      {caseDetail.workflow_slug === 'deposit_followup_v1' && (
        <div className="mb-4">
          <FollowupBriefPanel caseId={caseId} />
        </div>
      )}

      {/* Agent handoff affordance — only when the current agent has
          handed the case off and is viewing it after the fact. Sets
          expectations: review controls are locked, but the agent can
          still log a client interaction below (which is the only
          action available to them at this state). */}
      {showAgentHandoffBanner && <AgentHandoffBanner state={state} />}

      {/* Interaction recorder lives on every staff case (not only
          follow-ups): agents log every attempt — connected or not —
          so the CNBV cobranza report has accurate counts and the
          next brief can surface "last_contact" accurately. When the
          handoff banner is up, the recorder is contextually the only
          action available — the accent border draws the eye there. */}
      <div
        className="mb-4"
        style={
          showAgentHandoffBanner
            ? {
                borderLeft: '3px solid #818CF8',
                borderRadius: 4,
                paddingLeft: 12,
              }
            : undefined
        }
      >
        <InteractionRecorder primaryCaseId={caseId} />
      </div>

      {/* Appointments scheduled FOR this case (via the "Schedule
          callback" button on an interaction). Lives directly under
          the recorder so the agent confirms the appointment landed
          without leaving the page. */}
      <div className="mb-4">
        <CaseAppointmentsPanel caseId={caseId} />
      </div>

      {/* Enhanced due diligence — renders itself away on a case that
          requires none, which is most of them. */}
      {caseId && (
        <EddEvidencePanel
          caseId={caseId}
          canRecord={role === 'manager' || role === 'super_admin'}
        />
      )}

      {/* Member + assignee */}
      <CaseParties caseDetail={caseDetail} />

      {/* Contract documents — only relevant for contract_review_v1.
          KYC cases render their captured fields + file downloads via
          the workflow-generic section below. */}
      {isContractCase && contractSummary && signedDocs && (
        <section
          className="rounded-2xl p-5 mb-4"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h2 className="text-sm font-semibold mb-3" style={{ color: colors.muted }}>
            DOCUMENTS
          </h2>

          <div className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <div className="font-medium text-sm" style={{ color: colors.ink }}>
                Rendered contract (template)
              </div>
              <div className="text-xs" style={{ color: colors.mutedSoft }}>
                Generated {formatDateTime(contractSummary.rendered_at)}
              </div>
            </div>
            <button
              type="button"
              onClick={handleDownloadRendered}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
              style={buttonStyle('secondary', false)}
            >
              Download
            </button>
          </div>

          {signedDocs.length > 0 && (
            <div className="border-t mt-3 pt-3" style={{ borderColor: colors.borderSoft }}>
              <div className="text-xs font-semibold mb-2" style={{ color: colors.muted }}>
                SIGNED UPLOADS ({signedDocs.length})
              </div>
              <div className="space-y-2">
                {signedDocs.map((doc) => (
                  <div
                    key={doc.document_id}
                    className="flex items-center justify-between gap-3 p-2 rounded"
                    style={{ background: colors.canvas }}
                  >
                    <div className="min-w-0 text-sm">
                      <div className="font-medium" style={{ color: colors.ink }}>
                        signed_v{doc.sequence}.pdf
                      </div>
                      <div className="text-xs" style={{ color: colors.mutedSoft }}>
                        Uploaded {formatDateTime(doc.uploaded_at)} · {formatBytes(doc.size_bytes)} ·
                        {' '}sha256 <span className="font-mono">{doc.file_hash.slice(0, 12)}…</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDownloadSigned(doc.document_id)}
                      disabled={downloadingDocId === doc.document_id}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                      style={buttonStyle('secondary', downloadingDocId === doc.document_id)}
                    >
                      {downloadingDocId === doc.document_id ? '…' : 'Download'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Workflow-generic captured data — branches on state:
          · KYC + state ∈ (under_review, information_required)
              → split layout with per-field review controls + a
                right-side document viewer pane that opens when the
                agent clicks "View" on a file field. This is the
                granular review surface (turn 2 of the per-field
                review feature).
          · Anything else (contract, KYC in client-editable state,
              terminal) → the legacy read-only section. */}
      <CaseDataReview
        caseDetail={caseDetail}
        isContractCase={isContractCase}
        isReviewableKyc={isReviewableKyc}
        viewerFile={viewerFile}
        onViewDocument={setViewerFile}
        fieldReviewReadOnly={fieldReviewReadOnly}
        role={role}
        onReviewChanged={() => loadAll()}
        onError={setActionError}
      />

      {/* Pending-rejection banner — visible to staff only when an agent has
          proposed rejection. Shows the internal reason so the manager can
          decide without navigating to the timeline. The client NEVER sees
          this state as anything other than "in review" (SOD privacy gate). */}
      {caseDetail.current_state_slug === 'pending_rejection' && (
        <section
          className="rounded-2xl p-5 mb-4"
          style={{
            background: colors.dangerBg,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          <h2 className="text-sm font-semibold mb-2" style={{ color: colors.danger }}>
            REJECTION PROPOSED BY AGENT
          </h2>
          {caseDetail.agent_rejection_reason ? (
            <p className="text-sm" style={{ color: colors.ink }}>
              {caseDetail.agent_rejection_reason}
            </p>
          ) : (
            <p className="text-sm" style={{ color: colors.mutedSoft }}>
              No reason recorded.
            </p>
          )}
          <p className="text-xs mt-2" style={{ color: colors.mutedSoft }}>
            Use the actions below to approve the rejection (and write a client message)
            or to override and return the case to review.
          </p>
        </section>
      )}

      {/* Available actions */}
      {availableEvents.length > 0 && (
        <section
          className="rounded-2xl p-5 mb-4"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h2 className="text-sm font-semibold mb-3" style={{ color: colors.muted }}>
            AVAILABLE ACTIONS
          </h2>
          {isReviewableKyc && !allDecided && reviewTotal > 0 && (
            <p
              className="text-xs mb-3 rounded-lg px-3 py-2"
              style={{ background: colors.warningBg, color: colors.warning }}
            >
              Approve or reject <strong>every</strong> field and document ({decidedCount} of{' '}
              {reviewTotal} decided) before you can send the case back to the member or submit it
              to a supervisor.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {availableEvents.map((event) => {
              // Special-case REQUEST_INFO: the per-field review feature
              // replaced the old curated-text modal with a gated direct
              // fire. The button is disabled until every field is
              // decided AND at least one is rejected. The backend
              // builds the client-visible message from the per-field
              // rejection reasons.
              if (event === 'REQUEST_INFO') {
                return (
                  <SendBackButton
                    key={event}
                    actionInFlight={actionInFlight}
                    canSendBack={canSendBack}
                    allDecided={allDecided}
                    anyRejected={anyRejected}
                    onClick={() => handleSendBack()}
                  />
                );
              }
              // SUBMIT_FOR_DECISION: clean handoff to supervisor. Gated on the
              // all-decided sweep but only fires when NOTHING is rejected.
              if (event === 'SUBMIT_FOR_DECISION') {
                return (
                  <SubmitForDecisionButton
                    key={event}
                    actionInFlight={actionInFlight}
                    canSubmitForDecision={canSubmitForDecision}
                    allDecided={allDecided}
                    anyRejected={anyRejected}
                    onClick={() => handleSubmitForDecision()}
                  />
                );
              }
              // PROPOSE_REJECTION: same all-decided sweep but does NOT require
              // a rejection — AML / PEP / sanctions concerns live outside
              // per-field review.
              if (event === 'PROPOSE_REJECTION') {
                return (
                  <ProposeRejectionButton
                    key={event}
                    actionInFlight={actionInFlight}
                    canProposeRejection={canProposeRejection}
                    allDecided={allDecided}
                    onClick={() => setOpenModal('kyc_propose_rejection')}
                  />
                );
              }
              const quick = QUICK_ACTIONS[event];
              const pending = MODAL_ACTIONS[event];
              if (quick) {
                const isWorking = actionInFlight === quick.event;
                return (
                  <button
                    key={event}
                    type="button"
                    onClick={() => handleQuickAction(quick)}
                    disabled={isWorking || actionInFlight !== null}
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    style={buttonStyle(quick.variant, isWorking || actionInFlight !== null)}
                  >
                    {isWorking ? 'Working…' : quick.label}
                  </button>
                );
              }
              if (pending) {
                return (
                  <button
                    key={event}
                    type="button"
                    onClick={() => setOpenModal(pending.modal)}
                    disabled={actionInFlight !== null}
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    style={buttonStyle(pending.variant, actionInFlight !== null)}
                  >
                    {pending.label}
                  </button>
                );
              }
              return (
                <button
                  key={event}
                  type="button"
                  disabled
                  className="px-4 py-2 rounded-lg text-sm font-semibold"
                  style={buttonStyle('secondary', true)}
                >
                  {event}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Timeline */}
      <section
        className="rounded-2xl p-5"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <h2 className="text-sm font-semibold mb-3" style={{ color: colors.muted }}>
          AUDIT TRAIL
        </h2>
        {timeline.length === 0 ? (
          <p className="text-sm" style={{ color: colors.mutedSoft }}>
            No events recorded yet.
          </p>
        ) : (
          <ol className="space-y-3">
            {timeline.map((evt) => (
              <li
                key={evt.id}
                className="flex gap-3 items-start text-sm border-l-2 pl-3"
                style={{ borderColor: colors.border }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-x-2 items-baseline">
                    <span className="font-mono text-xs" style={{ color: colors.ink }}>
                      {evt.event}
                    </span>
                    <span className="text-xs" style={{ color: colors.mutedSoft }}>
                      {evt.from_state ? `${evt.from_state} → ${evt.to_state}` : evt.to_state}
                    </span>
                  </div>
                  <div className="text-xs" style={{ color: colors.muted }}>
                    by {evt.triggered_by_email ?? '—'} ({evt.actor_role}) · {formatDateTime(evt.created_at)}
                  </div>
                  {evt.notes && (
                    <div
                      className="mt-2 p-2 rounded text-xs"
                      style={{
                        background: colors.canvas,
                        color: colors.inkSoft,
                        border: `1px solid ${colors.borderSoft}`,
                      }}
                    >
                      <span className="font-semibold mr-1" style={{ color: colors.muted }}>
                        Internal:
                      </span>
                      {evt.notes}
                    </div>
                  )}
                  {evt.client_visible_notes && (
                    <div
                      className="mt-2 p-2 rounded text-xs"
                      style={{
                        background: colors.warningBg,
                        color: colors.inkSoft,
                        border: `1px solid ${colors.dangerBorder}`,
                      }}
                    >
                      <div className="font-semibold mb-1" style={{ color: colors.warning }}>
                        Client-visible message
                      </div>
                      <div className="mb-1">{evt.client_visible_notes.summary}</div>
                      {evt.client_visible_notes.issues.length > 0 && (
                        <ul className="list-disc pl-4 space-y-0.5">
                          {evt.client_visible_notes.issues.map((iss) => (
                            <li key={`${evt.id}:${iss}`}>{iss}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {openModal === 'return_to_client' && (
        <ReturnToClientModal
          caseId={caseId}
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Returned to the member with your curated message.');
            loadAll();
          }}
        />
      )}

      {openModal === 'return_to_agent' && (
        <SimpleReasonModal
          caseId={caseId}
          event="manager:return_to_agent"
          title="Return to agent"
          description="The case goes back to the agent's queue with your internal notes. The member is not notified or shown your reason."
          submitLabel="Return to agent"
          submitVariant="primary"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Returned to the agent.');
            loadAll();
          }}
        />
      )}

      {openModal === 'reject' && (
        <SimpleReasonModal
          caseId={caseId}
          event="manager:reject"
          title="Reject contract"
          description="This is final — the case closes as rejected. Your reason is recorded in the audit log; the member sees only that the contract was not approved."
          submitLabel="Reject contract"
          submitVariant="danger"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Contract rejected.');
            loadAll();
          }}
        />
      )}

      {/* KYC modal actions — all use SimpleReasonModal; KYC's
          information_required state is NOT in the backend's
          _CLIENT_FACING_RETURN_STATES, so these only collect internal
          notes (no curated client-facing message). */}
      {openModal === 'kyc_reject' && (
        <SimpleReasonModal
          caseId={caseId}
          event="REJECT"
          title="Reject KYC submission"
          description="Closes the case as rejected. The member is notified that their application was not approved; your reason stays in the audit log."
          submitLabel="Reject KYC"
          submitVariant="danger"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('KYC rejected.');
            loadAll();
          }}
        />
      )}

      {openModal === 'kyc_restrict' && (
        <SimpleReasonModal
          caseId={caseId}
          event="RESTRICT"
          title="Restrict for compliance review"
          description="The case is held for an additional compliance check. Your reason is recorded in the audit log and visible only to staff."
          submitLabel="Restrict"
          submitVariant="danger"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Case restricted.');
            loadAll();
          }}
        />
      )}

      {openModal === 'kyc_clear' && (
        <SimpleReasonModal
          caseId={caseId}
          event="CLEAR"
          title="Clear restriction"
          description="Move the case back to standard review. Your reason is recorded in the audit log."
          submitLabel="Clear restriction"
          submitVariant="primary"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Restriction cleared.');
            loadAll();
          }}
        />
      )}

      {openModal === 'kyc_archive' && (
        <SimpleReasonModal
          caseId={caseId}
          event="ARCHIVE"
          title="Archive case"
          description="Move this case into the legal-retention archive. Required for closed approved/rejected cases per retention rules."
          submitLabel="Archive"
          submitVariant="primary"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Case archived.');
            loadAll();
          }}
        />
      )}

      {/* Two-step rejection modals (onboarding_retail SOD flow) */}

      {openModal === 'kyc_propose_rejection' && (
        <SimpleReasonModal
          caseId={caseId}
          event="PROPOSE_REJECTION"
          title="Propose rejection"
          description="Write your internal reason for proposing rejection. This note is visible only to staff — the member never sees it. A manager will review and make the final decision."
          submitLabel="Propose rejection"
          submitVariant="danger"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Rejection proposed — a manager will review shortly.');
            loadAll();
          }}
        />
      )}

      {openModal === 'kyc_approve_rejection' && (
        <ApproveRejectionModal
          caseId={caseId}
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Rejection approved. The member has been notified.');
            loadAll();
          }}
        />
      )}

      {openModal === 'kyc_override_rejection' && (
        <SimpleReasonModal
          caseId={caseId}
          event="OVERRIDE_REJECTION"
          title="Override rejection proposal"
          description="Cancel the agent's rejection proposal and return the case to active review. Your reason is recorded in the audit log and visible to the agent."
          submitLabel="Override and return to review"
          submitVariant="primary"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Rejection overridden — case returned to review.');
            loadAll();
          }}
        />
      )}

      {openModal === 'kyc_reopen' && (
        <SimpleReasonModal
          caseId={caseId}
          event="REOPEN"
          title="Reopen case"
          description="Reopen this finalized KYC case and return it to active review. Your reason is recorded in the audit log. The member's post-approval access pauses until the case is approved again."
          submitLabel="Reopen and return to review"
          submitVariant="primary"
          onClose={() => setOpenModal(null)}
          onSubmitted={() => {
            setOpenModal(null);
            setActionFlash('Case reopened — returned to active review.');
            loadAll();
          }}
        />
      )}
    </>
  );
}

// ─── Captured data section (workflow-generic) ─────────────────────
interface CapturedDataSectionProps {
  caseId: string;
  data: Record<string, unknown>;
  fields: FormFieldDef[];
  /** Role of the viewing staff. Only super_admin gets a Download (file
   *  extraction) on the member's personal documents; everyone else gets a
   *  View that opens the doc inline in the viewer pane. */
  role?: string;
  onViewDocument: (file: ReviewableFile) => void;
  onError: (msg: string) => void;
}

interface FilePointer {
  document_id: string;
  storage_path?: string;
  size_bytes?: number;
  mime_type?: string;
  file_hash?: string;
}

function isFilePointer(value: unknown): value is FilePointer {
  if (typeof value !== 'object' || value === null) return false;
  return typeof (value as FilePointer).document_id === 'string';
}

/** Render every captured `case_data` entry, with download buttons for
 *  file fields (whose value is a JSON pointer object) and prettified
 *  text for everything else. Used by KYC and any other workflow that
 *  collects structured data. */
function CapturedDataSection({ caseId, data, fields, role, onViewDocument, onError }: Readonly<CapturedDataSectionProps>): JSX.Element {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const entries = Object.entries(data).filter(([key]) => !key.startsWith('_'));
  const fieldByKey = new Map(fields.map((f) => [f.field_key, f]));
  // Only super_admin may extract (download) the member's personal documents;
  // agents and managers view them inline in the viewer pane instead.
  const canDownload = role === 'super_admin';

  async function handleDownload(documentId: string): Promise<void> {
    setDownloadingId(documentId);
    try {
      const url = await workflowService.getDocumentDownloadUrl(caseId, documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      onError(getApiErrorMessage(err, 'Could not generate the download link.'));
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <section
      className="rounded-2xl p-5 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-sm font-semibold mb-3" style={{ color: colors.muted }}>
        CAPTURED DATA
      </h2>
      {entries.length === 0 ? (
        <p className="text-sm" style={{ color: colors.mutedSoft }}>
          The member has not submitted any data yet.
        </p>
      ) : (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {entries.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: colors.muted }}>
                {fieldByKey.get(key)?.label ?? key.replace(/_/g, ' ')}
              </dt>
              <dd className="break-words" style={{ color: colors.ink }}>
                {isFilePointer(value) ? (
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs" style={{ color: colors.mutedSoft }}>
                      {value.mime_type ?? 'file'}
                      {value.size_bytes !== undefined && ` · ${formatBytes(value.size_bytes)}`}
                    </span>
                    {canDownload ? (
                      <button
                        type="button"
                        onClick={() => handleDownload(value.document_id)}
                        disabled={downloadingId === value.document_id}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={buttonStyle('secondary', downloadingId === value.document_id)}
                      >
                        {downloadingId === value.document_id ? '…' : 'Download'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          onViewDocument({
                            fieldKey: key,
                            label: fieldByKey.get(key)?.label ?? key.replace(/_/g, ' '),
                            documentId: value.document_id,
                            mimeType: value.mime_type,
                          })
                        }
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={buttonStyle('secondary', false)}
                      >
                        View
                      </button>
                    )}
                  </span>
                ) : (
                  <span>{formatFieldValue(value, fieldByKey.get(key))}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}


// ─── Approve rejection modal ──────────────────────────────────────
// Used for APPROVE_REJECTION only — the manager writes a curated
// message that the client will see when their application is rejected.
// Distinct from SimpleReasonModal because it sends `client_rejection_message`
// instead of `notes`.

interface ApproveRejectionModalProps {
  readonly caseId: string;
  readonly onClose: () => void;
  readonly onSubmitted: () => void;
}

function ApproveRejectionModal({ caseId, onClose, onSubmitted }: ApproveRejectionModalProps): JSX.Element {
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = message.trim();
  const valid = trimmed.length >= 10 && trimmed.length <= 1000;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await workflowService.applyTransition(caseId, {
        event_trigger: 'APPROVE_REJECTION',
        client_rejection_message: trimmed,
      });
      onSubmitted();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not approve the rejection.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Approve rejection
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Write the message the member will see when their application is rejected.
        Keep it factual and avoid references to internal AML/compliance details.
      </p>

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      <div className="mb-4">
        <label
          htmlFor="reject-member-message"
          className="block text-xs font-semibold mb-1"
          style={{ color: colors.inkSoft }}
        >
          Member-facing rejection message
        </label>
        <textarea
          id="reject-member-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={busy}
          rows={5}
          maxLength={1000}
          placeholder="e.g. After reviewing your application, we are unable to approve it at this time. Please contact your Account Executive if you have questions."
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
        />
        <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
          {trimmed.length}/1000 · minimum 10 characters
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => submit()}
          disabled={busy || !valid}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy || !valid ? colors.disabledBg : colors.danger,
            color: busy || !valid ? colors.disabledText : '#FFFFFF',
            border: 'none',
          }}
        >
          {busy ? 'Approving…' : 'Approve rejection'}
        </button>
      </div>
    </Modal>
  );
}
